import { useRoles } from '@/features/admin/roles/hooks'
import type { VerificationMode } from '@/features/contract/types'

const ALL_MODES: VerificationMode[] = ['CLICK', 'DOCUMENT', 'COMMENT']

const MODE_LABELS: Record<VerificationMode, string> = {
  CLICK: 'Klik (potwierdzenie)',
  DOCUMENT: 'Wgranie dokumentu',
  COMMENT: 'Komentarz weryfikacyjny',
}

type Props = {
  modes: VerificationMode[]
  onModesChange: (m: VerificationMode[]) => void
  roleCodes: string[]
  onRoleCodesChange: (codes: string[]) => void
  disabled?: boolean
}

/**
 * Panel "Weryfikacja" w FieldFormSheet. Multi-select trybów + multi-select ról
 * uprawnionych. Walidacja FE: oba puste albo oba non-empty (mirror backend
 * chk_field_definition_verify_consistency).
 */
export function FieldVerificationConfig({
  modes, onModesChange, roleCodes, onRoleCodesChange, disabled,
}: Props) {
  const rolesQ = useRoles()
  const allRoles = (rolesQ.data ?? []).filter(r => r.isActive)

  const toggleMode = (m: VerificationMode) => {
    if (modes.includes(m)) onModesChange(modes.filter(x => x !== m))
    else onModesChange([...modes, m])
  }

  const toggleRole = (code: string) => {
    if (roleCodes.includes(code)) onRoleCodesChange(roleCodes.filter(x => x !== code))
    else onRoleCodesChange([...roleCodes, code])
  }

  return (
    <section className="flex flex-col gap-2 border-t border-slate-100 pt-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-600">
        Weryfikacja (ptaszek + audit)
      </h3>
      <p className="text-[11px] text-slate-400">
        Aktywuj weryfikację pola - pojawi się ikona ptaszka obok wartości. Wybierz tryby
        (co użytkownik musi zrobić, by zatwierdzić) oraz role osób uprawnionych.
      </p>

      <div className="grid gap-1">
        <span className="text-xs font-medium text-slate-700">Tryby weryfikacji</span>
        <div className="flex flex-wrap gap-2">
          {ALL_MODES.map(m => (
            <label
              key={m}
              className="flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2 py-1 text-xs cursor-pointer hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={modes.includes(m)}
                onChange={() => toggleMode(m)}
                disabled={disabled}
                className="size-3.5"
              />
              <span>{MODE_LABELS[m]}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid gap-1">
        <span className="text-xs font-medium text-slate-700">Role uprawnione do zatwierdzenia</span>
        {allRoles.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Brak aktywnych ról - dodaj rolę w /admin/roles.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {allRoles.map(r => (
              <label
                key={r.code}
                className="flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2 py-1 text-xs cursor-pointer hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={roleCodes.includes(r.code)}
                  onChange={() => toggleRole(r.code)}
                  disabled={disabled}
                  className="size-3.5"
                />
                <span>{r.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {(modes.length > 0) !== (roleCodes.length > 0) && (
        <p className="text-xs text-red-600">
          Tryby i role muszą być wybrane razem (lub oba puste - wyłącza weryfikację).
        </p>
      )}
    </section>
  )
}
